import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const VerificationCenter = ({ isOpen, onClose }) => {
  const { user, isAuthenticated, apiCall } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [verificationStatus, setVerificationStatus] = useState({
    email: { verified: false, address: '' },
    phone: { verified: false, number: '' },
    social: { verified: false, accounts: [] },
    identity: { verified: false },
    trustScore: 0,
    verificationLevel: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Phone verification
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneStep, setPhoneStep] = useState('enter'); // 'enter', 'verify'
  const [countdown, setCountdown] = useState(0);

  // Social media linking
  const [socialProviders] = useState([
    { id: 'google', name: 'Google', icon: '🔍', color: '#4285F4' },
    { id: 'facebook', name: 'Facebook', icon: '📘', color: '#1877F2' },
    { id: 'twitter', name: 'Twitter', icon: '🐦', color: '#1DA1F2' },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: '#0A66C2' }
  ]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchVerificationStatus();
    }
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const fetchVerificationStatus = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/verification/status');
      
      if (data.success) {
        setVerificationStatus(data.data);
      } else {
        // API endpoint not implemented yet
        console.log('Verification status API endpoint not yet implemented');
        setVerificationStatus({
          email: { verified: false, verifiedAt: null },
          phone: { verified: false, verifiedAt: null },
          identity: { verified: false, verifiedAt: null }
        });
      }
    } catch (error) {
      if (error.message.includes('404')) {
        // Endpoint doesn't exist yet - this is expected during development
        console.log('Verification status API endpoint not found - using default data');
      } else {
        console.error('Failed to fetch verification status:', error);
      }
      // Use default verification status during development
      setVerificationStatus({
        email: { verified: false, verifiedAt: null },
        phone: { verified: false, verifiedAt: null },
        identity: { verified: false, verifiedAt: null }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const sendEmailVerification = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await apiCall('/verification/email/send', {
        method: 'POST'
      });
      
      if (data.success) {
        alert('Verification email sent! Please check your inbox.');
      } else {
        setError(data.message || 'Failed to send verification email');
      }
    } catch (error) {
      setError('Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneVerification = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await apiCall('/verification/phone/send', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber })
      });
      
      if (data.success) {
        setPhoneStep('verify');
        setCountdown(300); // 5 minutes
      } else {
        setError(data.message || 'Failed to send verification code');
      }
    } catch (error) {
      setError('Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/verification/phone/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ code: verificationCode })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPhoneStep('enter');
        setVerificationCode('');
        setCountdown(0);
        fetchVerificationStatus();
        alert('Phone number verified successfully!');
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (error) {
      setError('Failed to verify phone number');
    } finally {
      setLoading(false);
    }
  };

  const linkSocialAccount = async (provider) => {
    try {
      setLoading(true);
      setError('');
      
      // This would typically open OAuth popup
      // For demo purposes, we'll simulate the process
      const response = await fetch('/api/verification/social/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ 
          provider,
          accessToken: 'demo_token_' + Date.now()
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        fetchVerificationStatus();
        alert(`${provider} account linked successfully!`);
      } else {
        setError(data.message || `Failed to link ${provider} account`);
      }
    } catch (error) {
      setError(`Failed to link ${provider} account`);
    } finally {
      setLoading(false);
    }
  };

  const unlinkSocialAccount = async (provider) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/verification/social/unlink', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ provider })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        fetchVerificationStatus();
        alert(`${provider} account unlinked successfully!`);
      } else {
        setError(data.message || `Failed to unlink ${provider} account`);
      }
    } catch (error) {
      setError(`Failed to unlink ${provider} account`);
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !isAuthenticated) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
          
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
            Account Verification
          </h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Verify your identity to build trust and unlock features
          </p>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: '16px 24px', backgroundColor: '#f9f9f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Verification Progress</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
              {verificationStatus.verificationLevel}/4
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(verificationStatus.verificationLevel / 4) * 100}%`,
              height: '100%',
              backgroundColor: '#4CAF50',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Trust Score: {verificationStatus.trustScore}/100</span>
            <div style={{
              padding: '2px 8px',
              backgroundColor: verificationStatus.trustScore >= 60 ? '#4CAF50' : 
                             verificationStatus.trustScore >= 30 ? '#FF9800' : '#f44336',
              color: 'white',
              borderRadius: '12px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              {verificationStatus.trustScore >= 60 ? 'Trusted' : 
               verificationStatus.trustScore >= 30 ? 'Verified' : 'New'}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9'
        }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'email', label: 'Email' },
            { id: 'phone', label: 'Phone' },
            { id: 'social', label: 'Social' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 8px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                color: activeTab === tab.id ? '#2196F3' : '#666',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #2196F3' : 'none'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {activeTab === 'overview' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Verification Status
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>✉️</span>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>Email Verification</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {verificationStatus.email.address}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 8px',
                    backgroundColor: verificationStatus.email.verified ? '#4CAF50' : '#f44336',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {verificationStatus.email.verified ? 'Verified' : 'Not Verified'}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>📱</span>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>Phone Verification</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {verificationStatus.phone.number || 'Not provided'}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 8px',
                    backgroundColor: verificationStatus.phone.verified ? '#4CAF50' : '#f44336',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {verificationStatus.phone.verified ? 'Verified' : 'Not Verified'}
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>🔗</span>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600' }}>Social Media</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {verificationStatus.social.accounts.length} account(s) linked
                      </div>
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 8px',
                    backgroundColor: verificationStatus.social.verified ? '#4CAF50' : '#f44336',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {verificationStatus.social.verified ? 'Linked' : 'Not Linked'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Email Verification
              </h3>
              
              {verificationStatus.email.verified ? (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#e8f5e8',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#2e7d32' }}>
                    Email Verified
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    {verificationStatus.email.address}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fff3e0',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#e65100' }}>
                      Your email is not verified. Please check your inbox for a verification link.
                    </div>
                  </div>
                  
                  <button
                    onClick={sendEmailVerification}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Verification Email'}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'phone' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Phone Verification
              </h3>
              
              {verificationStatus.phone.verified ? (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#e8f5e8',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#2e7d32' }}>
                    Phone Verified
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    {verificationStatus.phone.number}
                  </div>
                </div>
              ) : (
                <div>
                  {phoneStep === 'enter' ? (
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#333'
                        }}>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px'
                          }}
                        />
                      </div>
                      
                      <button
                        onClick={sendPhoneVerification}
                        disabled={loading || !phoneNumber}
                        style={{
                          width: '100%',
                          padding: '12px 24px',
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: (loading || !phoneNumber) ? 'not-allowed' : 'pointer',
                          opacity: (loading || !phoneNumber) ? 0.7 : 1
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Verification Code'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}>
                        <div style={{ fontSize: '14px', color: '#1976d2' }}>
                          We've sent a 6-digit code to {phoneNumber}
                        </div>
                        {countdown > 0 && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Code expires in {formatCountdown(countdown)}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#333'
                        }}>
                          Verification Code
                        </label>
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          placeholder="Enter 6-digit code"
                          maxLength="6"
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px',
                            textAlign: 'center'
                          }}
                        />
                      </div>
                      
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          onClick={verifyPhoneCode}
                          disabled={loading || verificationCode.length !== 6}
                          style={{
                            flex: 1,
                            padding: '12px 24px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer',
                            opacity: (loading || verificationCode.length !== 6) ? 0.7 : 1
                          }}
                        >
                          {loading ? 'Verifying...' : 'Verify Code'}
                        </button>
                        
                        <button
                          onClick={() => {
                            setPhoneStep('enter');
                            setVerificationCode('');
                            setCountdown(0);
                          }}
                          style={{
                            flex: 1,
                            padding: '12px 24px',
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Back
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'social' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Social Media Accounts
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {socialProviders.map((provider) => {
                  const isLinked = verificationStatus.social.accounts.some(
                    account => account.provider === provider.id
                  );
                  
                  return (
                    <div key={provider.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: provider.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '18px'
                        }}>
                          {provider.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '600' }}>
                            {provider.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {isLinked ? 'Connected' : 'Not connected'}
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => isLinked ? unlinkSocialAccount(provider.id) : linkSocialAccount(provider.id)}
                        disabled={loading}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: isLinked ? '#f44336' : provider.color,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.7 : 1
                        }}
                      >
                        {loading ? 'Processing...' : isLinked ? 'Unlink' : 'Link'}
                      </button>
                    </div>
                  );
                })}
              </div>
              
              {verificationStatus.social.accounts.length > 0 && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: '#e8f5e8',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontSize: '14px', color: '#2e7d32', fontWeight: '600' }}>
                    🎉 Great! You have {verificationStatus.social.accounts.length} social account(s) linked.
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    This increases your trust score and helps verify your identity.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationCenter;